import { useCallback, useEffect, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { IPagination } from "../../../types/pagination";
import { useNavigate } from "react-router-dom";
import { Delivery, IGetDeliveryProps } from "../../../services/Delivery";
import { ISelectOptions } from "../../../types/inputs";
import { Ibge } from "../../../services/Ibge";
import { z } from "zod";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useContextSite } from "../../../context/Context";
import { IDeliveryDTO } from "../../../types/delivery";
import { toast } from "react-toastify";
import { useSessionStorage } from "../../../hooks/useSessionStorage";

const schemaFilterDelivery = z.object({
  uf: z.string().min(1),
  cidade: z.string().min(1),
});

type FilterDeliveryType = z.infer<typeof schemaFilterDelivery>;

export const useAdminDeliverys = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const isMobile = useMediaQuery({ maxWidth: "640px" });
  const [pagination, setPagination] = useState<IPagination>({} as IPagination);
  const [numberPage, setNumberPage] = useState(0);
  const [usuario] = useSessionStorage("@usuarioMapa");
  const isAdmin = usuario?.roles?.includes("ROLE_ADMIN");

  const canEditDeliveries =
    isAdmin || usuario?.recursos?.includes("CONFIGURACAO_DELIVERY_EDITAR");
  const canAccessDeliveries =
    isAdmin || usuario?.recursos?.includes("CONFIGURACAO_DELIVERY_VER");
  const [cidadesOptions, setCidadesOptions] = useState<ISelectOptions[]>(
    [] as ISelectOptions[]
  );
  const [filterProps, setFilterProps] = useState<FilterDeliveryType>(
    {} as FilterDeliveryType
  );
  const [ufOptions, setUfOptions] = useState<ISelectOptions[]>(
    [] as ISelectOptions[]
  );
  const [deliverys, setDeliverys] = useState<IDeliveryDTO[]>(
    [] as IDeliveryDTO[]
  );
  const { setIsLoad } = useContextSite();

  const { control, handleSubmit, reset, watch, setValue } =
    useForm<FilterDeliveryType>({
      defaultValues: {
        uf: "",
        cidade: "",
      },
      resolver: zodResolver(schemaFilterDelivery),
    });

  function getDeliverys(props?: IGetDeliveryProps) {
    setIsLoad(true);
    Delivery.get({ size: 5, page: 0, ...props })
      .then(({ data }) => {
        setPagination({
          actualPage: data.number,
          totalPage: data.totalPages,
          totalRegister: data.totalElements,
        });
        setDeliverys(data.content);
      })
      .catch(
        ({
          response: {
            data: { mensagem },
          },
        }) => toast.error(mensagem)
      )
      .finally(() => {
        setIsLoad(false);
      });
  }

  function handleFilter(data: FilterDeliveryType) {
    getDeliverys({ ...data, page: 0 });
  }

  const getUfs = useCallback(() => {
    getDeliverys();

    Ibge.UFs()
      .then(({ data }) => {
        const options = data.map((item) => ({
          label: item.sigla,
          value: item.sigla,
          element: item,
        }));

        setUfOptions(options);
      })
      .catch(
        ({
          response: {
            data: { mensagem },
          },
        }) => toast.error(mensagem)
      );
  }, []);

  useEffect(() => {
    getUfs();
  }, [getUfs]);

  useEffect(() => {
    getDeliverys({ page: numberPage, ...filterProps });
  }, [numberPage, filterProps]);

  useEffect(() => {
    if (!isOpen) {
      reset();
      setFilterProps({});
    }
  }, [isOpen]);

  function handleReset() {
    getDeliverys();
    setFilterProps({});
    setNumberPage(0);
  }

  useEffect(() => {
    if (watch("uf")) {
      setValue("cidade", "");
      Ibge.CidadesPorEstado({ sigla: watch("uf") }).then(({ data }) => {
        const options = data.map((item) => ({
          value: item.nome,
          label: item.nome,
          element: item,
        }));
        setCidadesOptions(options);
      });
    }
  }, [watch("uf")]);

  return {
    isOpen,
    setIsOpen,
    isMobile,
    pagination,
    setNumberPage,
    navigate,
    cidadesOptions,
    ufOptions,
    control,
    handleSubmit,
    reset,
    watch,
    Controller,
    handleFilter,
    deliverys,
    handleReset,
    canEditDeliveries,
    canAccessDeliveries,
  };
};
